import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  viewDataById,
  createNewData,
  updateDataById,
  deleteDataById,
} from '../../services/hls-transcoder.service';
import ConfirmPopup from '../common/confirm-popup.component';
import Iconify from '../common/iconify.component';

const AddEditBucket = ({ dataId, rowData, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      bucket: '',
      region: '',
      fileUrl: '',
      accessKey: '',
      secretKey: '',
      sourcePath: '/new',
      destinationPath: '/encoded',
      extensions: 'mkv,flv,avi,mts,mov,wmv,amv,mp4,mpg,mpeg,m4v',
      apiToken:
        'Q7bcupwu9v48dUA8FCDMZw7L2ZqoUOfH$ZWMyMDkyNjctNTA1ZS00NDYzLTk5YTktYWU3ZDc2YmY0ZjU0',
      apiLogin: 'javalawyer@gmail.com',
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteDataById(dataId)
      .then(() => {
        setShowDeleteConfirmation(false);
        onSuccess('Bucket deleted successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  };

  const saveNewData = (payload) => {
    createNewData(payload)
      .then(() => {
        onSuccess('New Bucket created successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateDataById(dataId, payload)
      .then(() => {
        onSuccess('Bucket settings updated successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    if (dataId) {
      updateExistingData(data);
    } else {
      saveNewData(data);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // Reset form to remove form validation error
    reset();

    viewDataById(dataId)
      .then((res) => {
        // Fill form values
        setValue('name', res.data?.name || '');
        setValue('bucket', res.data?.bucket || '');
        setValue('region', res.data?.region || '');
        setValue('fileUrl', res.data?.fileUrl || '');
        setValue('accessKey', res.data?.accessKey || '');
        setValue('secretKey', res.data?.secretKey || '');
        setValue('sourcePath', res.data?.sourcePath || '');
        setValue('destinationPath', res.data?.destinationPath || '');
        setValue('extensions', res.data?.extensions?.join(',') || '');
        setValue('apiToken', res.data?.apiToken || '');
        setValue('apiLogin', res.data?.apiLogin || '');

        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <>
      {loading && <Loading />}

      {!loading && (
        <>
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(50),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      type="text"
                      error={!!errors.name}
                      helperText={errors?.name?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} />

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="bucket"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Bucket"
                      type="text"
                      error={!!errors.bucket}
                      helperText={errors?.bucket?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="region"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Region"
                      type="text"
                      error={!!errors.region}
                      helperText={errors?.region?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="fileUrl"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...validationRules.CHANNEL_DOMAIN_URL,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CDN Resource URL"
                      type="text"
                      error={!!errors.fileUrl}
                      helperText={errors?.fileUrl?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="accessKey"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Access Key"
                      type="text"
                      error={!!errors.accessKey}
                      helperText={errors?.accessKey?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="secretKey"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Secret Key"
                      type={showSecretKey ? 'text' : 'password'}
                      error={!!errors.secretKey}
                      helperText={errors?.secretKey?.message || null}
                      fullWidth
                      variant="filled"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowSecretKey(!showSecretKey)}
                              edge="end"
                              title={
                                !showSecretKey
                                  ? 'Show secret key'
                                  : 'Hide secret key'
                              }
                            >
                              <Iconify
                                icon={
                                  !showSecretKey
                                    ? 'eva:eye-fill'
                                    : 'eva:eye-off-fill'
                                }
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="sourcePath"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Source Path"
                      type="text"
                      error={!!errors.sourcePath}
                      helperText={errors?.sourcePath?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="destinationPath"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Destination Path"
                      type="text"
                      error={!!errors.destinationPath}
                      helperText={errors?.destinationPath?.message || null}
                      fullWidth
                      disabled
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="extensions"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Extensions (Comma-Seperated)"
                      type="text"
                      error={!!errors.extensions}
                      helperText={errors?.extensions?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="apiToken"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="API Token"
                      type="text"
                      error={!!errors.apiToken}
                      helperText={errors?.apiToken?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="apiLogin"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="API Login"
                      type="text"
                      error={!!errors.apiLogin}
                      helperText={errors?.apiLogin?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
          >
            <Grid>
              <Button
                type="submit"
                form="add-edit-form"
                color="primary"
                variant="contained"
                disabled={loading || formSubmitted}
                sx={{ marginRight: 1 }}
              >
                {!dataId ? 'Save New' : 'Update Data'}
              </Button>
              {!!dataId && (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    onCancel();
                    reset();
                  }}
                  disabled={formSubmitted}
                >
                  Cancel
                </Button>
              )}
            </Grid>
            {!!dataId && (
              <Grid>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={formSubmitted}
                >
                  Delete
                </Button>
              </Grid>
            )}
          </Stack>
        </>
      )}

      {showDeleteConfirmation && (
        <ConfirmPopup
          title="Delete Bucket"
          message={`Do you want to delete ${rowData.name} Bucket? You can not undo this action!`}
          onCancel={() => setShowDeleteConfirmation(false)}
          onSuccess={() => handleDeleteData()}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

AddEditBucket.propTypes = {
  dataId: PropTypes.number.isRequired,
  rowData: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddEditBucket;
